// ─────────────────────────────────────────────────────────────
// Superset viz_type → renderer/category/icon mapping.
// `draw` is the module's internal render key; `cat` drives the
// chart-panel filter tabs; `icon` is a ChartIcon key.
// ─────────────────────────────────────────────────────────────
import SUPERSET_CANVAS_CONFIG from '../superset.canvas.config';

const DRAW_MAP = {
  bar: 'bar',
  dist_bar: 'bar',
  horizontal_bar: 'bar',
  histogram: 'bar',
  line: 'line',
  dual_line: 'line',
  area: 'area',
  pie: 'pie',
  donut: 'donut',
  big_number: 'bignumber',
  big_number_total: 'bignumber',
  table: 'table',
  pivot_table: 'pivottable',
  pivot_table_v2: 'pivottable',
  scatter: 'scatter',
  bubble: 'bubble',
  heatmap: 'heatmap',
  box_plot: 'boxplot',
  treemap: 'treemap',
  treemap_v2: 'treemap',
  sunburst: 'sunburst',
  sunburst_v2: 'sunburst',
  funnel: 'funnel',
  waterfall: 'waterfall',
  radar: 'radar',
  cal_heatmap: 'calendar',
  horizon: 'horizon',
  nightingale: 'nightingale',
  sankey: 'sankey',
  chord: 'chord',
  force: 'force',
  graph_chart: 'force',
  directed_force: 'force',
  partition: 'partition',
  word_cloud: 'wordcloud',
  gauge_chart: 'gauge',
  bullet: 'bullet',
  country_map: 'geomap',
  world_map: 'geomap',
  deck_geojson: 'geomap',
  mapbox: 'mapbox',
  deck_scatter: 'deckscatter',
  mixed_timeseries: 'mixed',
  echarts_timeseries_bar: 'bar',
  echarts_timeseries_line: 'line',
  echarts_timeseries_area: 'area',
  echarts_timeseries_scatter: 'scatter',
  echarts_timeseries_smooth: 'line',
  echarts_timeseries_step: 'line',
  echarts_area: 'area',
  echarts_bar: 'bar',
  echarts_pie: 'pie',
  echarts_radar: 'radar',
  echarts_funnel: 'funnel',
  echarts_mixed_timeseries: 'mixed',
  echarts_waterfall: 'waterfall',
  echarts_bubble_chart: 'bubble',
  echarts_box_plot: 'boxplot',
  paired_ttest: 'pairedbar',
  parallel_coordinates: 'parallel',
  event_flow: 'eventflow',
  violin: 'violin',
};

const CATEGORY_MAP = {
  popular: [
    'bar',
    'line',
    'area',
    'pie',
    'donut',
    'big_number',
    'big_number_total',
    'table',
    'pivot_table',
    'pivot_table_v2',
    'scatter',
    'echarts_bar',
    'echarts_pie',
    'echarts_timeseries_bar',
    'echarts_timeseries_line',
  ],
  time: ['cal_heatmap', 'horizon', 'mixed_timeseries', 'echarts_mixed_timeseries', 'echarts_timeseries_smooth', 'echarts_timeseries_step', 'echarts_area'],
  part: ['funnel', 'treemap', 'treemap_v2', 'sunburst', 'sunburst_v2', 'nightingale', 'partition'],
  comparison: ['waterfall', 'radar', 'echarts_radar', 'paired_ttest', 'bullet'],
  distribution: ['heatmap', 'box_plot', 'echarts_box_plot', 'histogram', 'violin', 'bubble', 'echarts_bubble_chart', 'parallel_coordinates'],
  flow: ['sankey', 'chord', 'force', 'graph_chart', 'directed_force', 'event_flow'],
  geo: ['country_map', 'world_map', 'mapbox', 'deck_scatter', 'deck_geojson'],
};

export function mapVizType(vizType) {
  return DRAW_MAP[vizType] || 'bar';
}

export function categorize(vizType) {
  if (!vizType) return 'other';
  if (CATEGORY_MAP.popular.includes(vizType)) return 'popular';
  if (vizType.includes('timeseries') || CATEGORY_MAP.time.includes(vizType)) return 'time';
  const hit = ['part', 'comparison', 'distribution', 'flow', 'geo'].find(key => CATEGORY_MAP[key].includes(vizType));
  return hit || 'other';
}

// Returns a ChartIcon key (see Components/ChartIcon.jsx)
export function getIconKey(vizType) {
  const v = vizType || '';
  if (!v) return 'bar';
  if (v.includes('bar') || v === 'dist_bar' || v === 'histogram' || v.includes('waterfall')) return 'bar';
  if (v.includes('line') || v.includes('area') || v.includes('timeseries') || v === 'horizon') return 'line';
  if (v === 'pie' || v === 'echarts_pie') return 'pie';
  if (v === 'donut') return 'donut';
  if (v.includes('big_number')) return 'trending';
  if (v === 'table' || v.includes('pivot_table')) return 'table';
  if (v.includes('scatter') || v === 'bubble' || v === 'echarts_bubble_chart') return 'dots';
  if (v === 'heatmap' || v === 'cal_heatmap') return 'grid';
  if (v.includes('treemap')) return 'layout';
  if (v.includes('funnel')) return 'filter';
  if (v === 'radar' || v === 'echarts_radar' || v === 'nightingale') return 'radar';
  if (v.includes('map') || v.includes('deck') || v === 'mapbox') return 'map';
  if (v === 'sankey') return 'split';
  if (v === 'chord') return 'vector';
  if (v.includes('force') || v === 'graph_chart') return 'topology';
  if (v === 'word_cloud') return 'language';
  if (v === 'gauge_chart') return 'gauge';
  if (v.includes('sunburst')) return 'circle';
  if (v === 'parallel_coordinates') return 'arrows';
  return 'bar';
}

export function getColor(vizType) {
  return SUPERSET_CANVAS_CONFIG.CATEGORY_COLORS[categorize(vizType)]?.bg ?? '#EEEDFE';
}

export function getIconColor(vizType) {
  return SUPERSET_CANVAS_CONFIG.CATEGORY_COLORS[categorize(vizType)]?.ic ?? '#3C3489';
}

const safeParse = value => {
  if (!value) return {};
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    return {};
  }
};

// Superset /api/v1/chart/ row → panel chart item
export function normalizeChart(raw) {
  const viz = raw.viz_type;
  return {
    id: raw.slice_id || raw.id,
    slice_id: raw.slice_id || raw.id,
    chart_id: raw.id,
    slice_name: raw.slice_name || `Chart ${raw.id}`,
    viz_type: viz,
    draw: mapVizType(viz),
    cat: categorize(viz),
    icon: getIconKey(viz),
    color: getColor(viz),
    ic: getIconColor(viz),
    datasource_id: raw.datasource_id,
    datasource_type: raw.datasource_type,
    datasource_name: raw.datasource_name_text,
    params: safeParse(raw.params),
    description: raw.description,
    thumbnail_url: raw.thumbnail_url,
    url: raw.url,
    changed_on_humanized: raw.changed_on_humanized,
    owners: raw.owners,
  };
}

export { safeParse };
